import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useInView, useSpring } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Phone, 
  MessageCircle, 
  Brain, 
  MapPin, 
  Zap, 
  Check, 
  ChevronRight, 
  ArrowRight, 
  Building,
  Sparkles,
  ListChecks,
  Clock,
  Mail,
  Globe,
  Menu,
  X,
  Play,
  Pause,
  Volume2,
  SkipForward,
  Calendar,
  ShoppingBag,
  FileText
} from 'lucide-react';
import { Button } from '../../components/ui/button';

// Refined animated section with more subtle and cinematic animations
const AnimatedSection = ({ children, delay = 0, className = "", direction = "up" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10% 0px" });

  // Define different initial states based on direction
  const getInitialState = useCallback(() => {
    switch(direction) {
      case "left": return { opacity: 0, x: -40 };
      case "right": return { opacity: 0, x: 40 };
      default: return { opacity: 0, y: 30 }; // "up" is default
    }
  }, [direction]);

  // Define final states based on direction
  const getFinalState = useCallback(() => {
    switch(direction) {
      case "left": 
      case "right": return { opacity: 1, x: 0 };
      default: return { opacity: 1, y: 0 };
    }
  }, [direction]);

  return (
    <motion.div
      ref={ref}
      initial={getInitialState()}
      animate={isInView ? getFinalState() : getInitialState()}
      transition={{ 
        duration: 0.9, 
        delay: delay,
        ease: [0.22, 1, 0.36, 1] // Custom cubic-bezier for smoother animation
      }}
      className={className}
      style={{ 
        willChange: "opacity, transform", 
        backfaceVisibility: "hidden",
        WebkitFontSmoothing: "subpixel-antialiased"
      }}
    >
      {children}
    </motion.div>
  );
};

// Audio Player component with Jobs-inspired minimalism
const AudioPlayer = ({ title, description, audioSrc, iconType = "phone", delay = 0 }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const intervalRef = useRef(null);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10% 0px" });

  // Format time in MM:SS
  const formatTime = (time) => {
    if (isNaN(time)) return "00:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Get icon based on type
  const getIcon = () => {
    switch(iconType) {
      case "appointment": return <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />;
      case "order": return <ShoppingBag className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />;
      case "inquiry": return <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />;
      default: return <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />;
    }
  };

  // Load audio metadata
  useEffect(() => {
    const audio = audioRef.current;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    if (audio) {
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    }

    return () => {
      if (audio) {
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      }
    };
  }, [audioSrc]);

  // Update progress during playback
  useEffect(() => {
    const audio = audioRef.current;

    const updateProgress = () => {
      if (audio) {
        setProgress(audio.currentTime);

        // If audio finished playing, reset
        if (audio.currentTime === audio.duration) {
          setIsPlaying(false);
          clearInterval(intervalRef.current);
        }
      }
    };

    if (isPlaying) {
      intervalRef.current = setInterval(updateProgress, 100);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isPlaying]);

  // Handle play/pause
  const togglePlay = () => {
    const audio = audioRef.current;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }

    setIsPlaying(!isPlaying);
  };

  // Handle click on progress bar
  const handleProgressClick = (e) => {
    const audio = audioRef.current;
    const progressBar = progressRef.current;

    if (audio && progressBar) {
      const rect = progressBar.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      audio.currentTime = pos * audio.duration;
      setProgress(audio.currentTime);
    }
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ 
        duration: 0.9, 
        delay: isInView ? delay : 0,
        ease: [0.22, 1, 0.36, 1]
      }}
      style={{ willChange: "transform, opacity" }}
    >
      <div className="bg-white rounded-xl p-5 sm:p-6 border border-gray-100 shadow-[0_2px_10px_-3px_rgba(0,0,0,0.04)] transition-all duration-500 hover:shadow-[0_12px_24px_-12px_rgba(0,0,0,0.1)]">
        <div className="flex items-center mb-4">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-50 flex items-center justify-center mr-3">
            {getIcon()}
          </div>
          <div>
            <h3 className="text-sm sm:text-base font-medium text-gray-900">{title}</h3>
            <p className="text-xs sm:text-sm text-gray-500">{description}</p>
          </div>
        </div>

        <div className="flex items-center mb-3">
          <button 
            onClick={togglePlay}
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0 transition-colors duration-200"
          >
            {isPlaying ? 
              <Pause className="w-4 h-4 sm:w-5 sm:h-5 text-white" /> : 
              <Play className="w-4 h-4 sm:w-5 sm:h-5 text-white ml-0.5" />
            }
          </button>

          <div className="flex-grow relative">
            <div 
              ref={progressRef}
              className="h-1.5 sm:h-2 bg-gray-100 rounded-full overflow-hidden cursor-pointer w-full"
              onClick={handleProgressClick}
            >
              <div 
                className="h-full bg-blue-500 rounded-full"
                style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }}
              ></div>
            </div>
            <div className="flex justify-between mt-1 text-xs text-gray-400">
              <span>{formatTime(progress)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>
        </div>

        <audio ref={audioRef} src={audioSrc} preload="metadata" />
      </div>
    </motion.div>
  );
};

// Carousel-style audio showcase with Jobs-inspired animation and interaction
const AudioShowcase = ({ onGetStarted }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeAudio, setActiveAudio] = useState(null);
  const showcaseRef = useRef(null);
  const isInView = useInView(showcaseRef, { once: true, margin: "-10% 0px" });

  // Real-life moments showcasing freedom for small business owners
  const businessMoments = [
    {
      id: 1,
      title: "Saturday, 9:30 AM",
      subtitle: "You're at your kid's game",
      headline: "Your customer needs something urgently",
      description: "Your store would normally be packed. But today, you're watching your daughter score her first goal. Meanwhile, your AI assistant handles the entire interaction flawlessly.",
      icon: Calendar,
      color: "blue",
      audioSrc: "https://docs.google.com/uc?export=download&id=1g6dwF9e3cUYW-L_jk29AjmOKEU_1O_od", // Demo audio file
      businessContext: "Helen hasn't missed a Saturday game in 6 months"
    },
    {
      id: 2,
      title: "Tuesday, 10:15 PM",
      subtitle: "You're already asleep",
      headline: "Large order comes in after hours",
      description: "Late night orders used to mean late nights for you. Not anymore. While you sleep, every detail is captured perfectly, and you wake up to everything organized and ready.",
      icon: Clock,
      color: "indigo",
      audioSrc: "https://docs.google.com/uc?export=download&id=1g6dwF9e3cUYW-L_jk29AjmOKEU_1O_od", // Demo audio file
      businessContext: "Michael now sleeps 8 hours every night"
    },
    {
      id: 3,
      title: "Thursday, 3:45 PM",
      subtitle: "You're with a customer",
      headline: "Phone rings with a complex inquiry",
      description: "Your undivided attention was once impossible. Now, while you focus on the customer in front of you, another receives expert guidance as if you were helping them personally.",
      icon: Phone,
      color: "purple",
      audioSrc: "https://docs.google.com/uc?export=download&id=1g6dwF9e3cUYW-L_jk29AjmOKEU_1O_od", // Demo audio file
      businessContext: "Sarah hasn't missed an important call in 3 months"
    },
    {
      id: 4,
      title: "Monday, 8:05 AM",
      subtitle: "You're stuck in traffic",
      headline: "Customer has an emergency",
      description: "Emergencies used to derail your entire day. This time, your AI turns crisis into opportunity while you're caught in morning traffic. By the time you arrive, it's already handled.",
      icon: Zap,
      color: "amber",
      audioSrc: "https://docs.google.com/uc?export=download&id=1g6dwF9e3cUYW-L_jk29AjmOKEU_1O_od", // Demo audio file
      businessContext: "James increased emergency response by 40%"
    },
    {
      id: 5,
      title: "Friday, 7:20 PM",
      subtitle: "You're at dinner with family",
      headline: "Customer can't make it before closing",
      description: "Your Friday nights are yours again. No more waiting for late pickups. While you enjoy dinner with your family, everything is arranged perfectly, and you never have to cut your evening short.",
      icon: MessageCircle,
      color: "green",
      audioSrc: "https://docs.google.com/uc?export=download&id=1g6dwF9e3cUYW-L_jk29AjmOKEU_1O_od", // Demo audio file
      businessContext: "David hasn't worked a Friday night in 4 months"
    }
  ];

  // Stop any playing audio when changing moments
  useEffect(() => {
    if (activeAudio) {
      activeAudio.pause();
      activeAudio.currentTime = 0;
      setIsPlaying(false);
    }
  }, [currentIndex]);

  // Handle next slide with cinematic transition
  const handleNext = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev === businessMoments.length - 1 ? 0 : prev + 1));
    setTimeout(() => setIsAnimating(false), 400);
  };

  // Handle previous slide with cinematic transition
  const handlePrev = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev === 0 ? businessMoments.length - 1 : prev - 1));
    setTimeout(() => setIsAnimating(false), 400);
  };

  // Handle dot navigation
  const handleDotClick = (index) => {
    if (isAnimating || index === currentIndex) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    setTimeout(() => setIsAnimating(false), 400);
  };

  // Toggle audio playback
  const togglePlay = () => {
    const audio = document.getElementById(`audio-example-${currentIndex}`);

    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      const playPromise = audio.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setActiveAudio(audio);
            setIsPlaying(true);
          })
          .catch(error => {
            console.error("Audio play failed:", error);
            setIsPlaying(false);
          });
      }
    }

    // Handle audio end
    audio.onended = () => {
      setIsPlaying(false);
    };
  };

  // Animation variants for smooth cinematic transitions
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.2
      } 
    }
  };

  const itemVariants = {
    hidden: { y: 15, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: {
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const currentMoment = businessMoments[currentIndex];
  const Icon = currentMoment.icon;

  // Responsive design that adapts to mobile and desktop
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <motion.div 
      ref={showcaseRef}
      className="mx-auto"
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={containerVariants}
    >
      <div className="bg-white rounded-xl shadow-[0_4px_24px_-8px_rgba(0,0,0,0.08)] border border-gray-100 overflow-hidden">
        <div className="grid md:grid-cols-2">
          {/* Left side - The moment */}
          <div className="p-6 sm:p-8 md:p-10">
            <motion.div 
              key={`title-${currentIndex}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="flex items-center space-x-2 mb-2"
            >
              <div className={`w-8 h-8 rounded-full bg-${currentMoment.color}-50 flex items-center justify-center`}>
                <Icon className={`w-4 h-4 text-${currentMoment.color}-600`} />
              </div>
              <div>
                <div className="text-base font-medium text-gray-900">{currentMoment.title}</div>
                <div className="text-sm text-gray-500">{currentMoment.subtitle}</div>
              </div>
            </motion.div>

            <motion.h3
              key={`headline-${currentIndex}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-xl sm:text-2xl font-semibold text-gray-900 mt-4 mb-3"
            >
              {currentMoment.headline}
            </motion.h3>

            <motion.p
              key={`description-${currentIndex}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-base text-gray-600 mb-4 leading-relaxed"
            >
              {currentMoment.description}
            </motion.p>

            <motion.div
              key={`context-${currentIndex}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="text-sm italic text-gray-500"
            >
              "{currentMoment.businessContext}"
            </motion.div>
          </div>

          {/* Right side - The experience */}
          <div className={`relative bg-${currentMoment.color}-50 p-6 sm:p-8 md:p-10 flex flex-col justify-center items-center`}>
            <motion.div
              key={`audio-${currentIndex}`}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.5 }}
              className="w-full max-w-sm"
            >
              <div className="text-center mb-6">
                <div className="text-sm font-medium text-gray-700 mb-1">Hear this moment</div>
                <div className="text-sm text-gray-500">A real call, a real business owner who wasn't there</div>
              </div>

              <div className="relative w-full aspect-square max-w-[240px] mx-auto mb-6">
                <div className={`absolute inset-0 rounded-full bg-${currentMoment.color}-100 animate-pulse-subtle`}></div>
                <motion.div 
                  className={`absolute inset-[15%] rounded-full bg-${currentMoment.color}-200 flex items-center justify-center cursor-pointer`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={togglePlay}
                >
                  <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-${currentMoment.color}-600 flex items-center justify-center shadow-lg`}>
                    {isPlaying ? (
                      <Pause className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                    ) : (
                      <Play className="w-6 h-6 sm:w-8 sm:h-8 text-white ml-1" />
                    )}
                  </div>
                </motion.div>

                {/* Audio wave animation */}
                {isPlaying && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-full h-full rounded-full flex items-center justify-center">
                      {[...Array(8)].map((_, i) => (
                        <div 
                          key={i}
                          className={`w-[2px] sm:w-1 h-4 sm:h-6 mx-[2px] sm:mx-1 bg-${currentMoment.color}-400 opacity-75`}
                          style={{
                            animation: `growHeight 0.${8 - i}s ease-in-out infinite alternate`,
                            animationDelay: `0.${i}s`,
                          }}
                        ></div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <audio 
                id={`audio-example-${currentIndex}`} 
                src={currentMoment.audioSrc}
                preload="metadata"
              />

              {/* Call to action for the specific moment */}
              <div className="text-center mt-2">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`px-4 py-2 text-sm font-medium text-${currentMoment.color}-700 bg-${currentMoment.color}-100 rounded-lg border border-${currentMoment.color}-200`}
                  onClick={onGetStarted}
                >
                  Make This Your Reality
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Navigation controls */}
      <div className="flex justify-between items-center mt-8">
        <button
          onClick={handlePrev}
          className="w-10 h-10 rounded-full bg-white shadow border border-gray-100 flex items-center justify-center hover:border-gray-200 transition-all duration-200"
          disabled={isAnimating}
        >
          <ChevronRight className="w-5 h-5 text-gray-700 rotate-180" />
        </button>

        <div className="flex space-x-2">
          {businessMoments.map((_, index) => (
            <button
              key={`dot-${index}`}
              onClick={() => handleDotClick(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentIndex 
                  ? `w-4 h-2 bg-${businessMoments[index].color}-600` 
                  : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Go to moment ${index + 1}`}
            />
          ))}
        </div>

        <button
          onClick={handleNext}
          className="w-10 h-10 rounded-full bg-white shadow border border-gray-100 flex items-center justify-center hover:border-gray-200 transition-all duration-200"
          disabled={isAnimating}
        >
          <ChevronRight className="w-5 h-5 text-gray-700" />
        </button>
      </div>

      {/* CSS for audio wave animation */}
      <style jsx>{`
        @keyframes growHeight {
          0% {
            height: 8px;
          }
          100% {
            height: 24px;
          }
        }

        @media (min-width: 640px) {
          @keyframes growHeight {
            0% {
              height: 12px;
            }
            100% {
              height: 36px;
            }
          }
        }
      `}</style>
    </motion.div>
  );
};

// Redesigned feature card with cleaner aesthetic
const FeatureCard = ({ icon, title, description, delay = 0 }) => {
  const Icon = icon;
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10% 0px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ 
        duration: 0.8, 
        delay: isInView ? delay : 0,
        ease: [0.22, 1, 0.36, 1]
      }}
      style={{ willChange: "transform, opacity" }}
    >
      <motion.div
        className="bg-white rounded-xl p-5 sm:p-7 md:p-8 h-full border border-gray-100 shadow-[0_2px_10px_-3px_rgba(0,0,0,0.04)] transition-all duration-500"
        whileHover={{ 
          y: -4, 
          boxShadow: "0 16px 30px -12px rgba(0, 0, 0, 0.08)",
          borderColor: "rgba(59, 130, 246, 0.15)" 
        }}
      >
        <div className="w-9 sm:w-11 h-9 sm:h-11 rounded-full bg-blue-50 flex items-center justify-center mb-4 sm:mb-5">
          <Icon className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-blue-600" />
        </div>
        <h3 className="text-base sm:text-[17px] font-medium mb-2 sm:mb-3 text-gray-900 tracking-tight">{title}</h3>
        <p className="text-sm sm:text-[15px] text-gray-600 leading-relaxed">{description}</p>
      </motion.div>
    </motion.div>
  );
};

// Refined testimonial card with more sophisticated animations
const TestimonialCard = ({ quote, author, company, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10% 0px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ 
        duration: 0.8, 
        delay: isInView ? delay : 0,
        ease: [0.22, 1, 0.36, 1]
      }}
      style={{ willChange: "transform, opacity" }}
    >
      <motion.div
        className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 h-full flex flex-col"
        whileHover={{ 
          y: -8,
          boxShadow: "0 20px 40px -20px rgba(0, 0, 0, 0.1)"
        }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex-grow">
          <div className="text-5xl font-serif text-blue-100 leading-none mb-4">"</div>
          <p className="text-gray-700 mb-6 leading-relaxed">{quote}</p>
        </div>
        <div>
          <div className="font-medium text-gray-900">{author}</div>
          <div className="text-gray-500 text-sm">{company}</div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Redesigned pricing card with cleaner aesthetic
const PricingCard = ({ title, price, features, isPopular, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10% 0px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ 
        duration: 0.8, 
        delay: isInView ? delay : 0,
        ease: [0.22, 1, 0.36, 1]
      }}
      style={{ willChange: "transform, opacity" }}
    >
      <motion.div 
        className={`
          relative rounded-xl overflow-hidden h-full flex flex-col
          ${isPopular ? 'border-2 border-blue-400 shadow-md' : 'border border-gray-200 shadow-sm'} 
        `}
        whileHover={{ 
          y: -8, 
          boxShadow: "0 20px 40px -20px rgba(0, 0, 0, 0.1)",
        }}
        transition={{ duration: 0.5 }}
      >
        {isPopular && (
          <div className="absolute top-0 right-0">
            <div className="bg-blue-500 text-white text-xs font-medium px-3 py-1 rounded-bl-lg">
              Most Popular
            </div>
          </div>
        )}
        <div className={`p-8 ${isPopular ? 'bg-blue-50' : 'bg-white'}`}>
          <h3 className="text-xl font-medium mb-2 text-gray-900">{title}</h3>
          <div className="mb-6">
            <span className="text-3xl font-bold text-gray-900">${price}</span>
            <span className="text-gray-500 text-sm">/month</span>
          </div>
          <Button 
            className={`w-full ${isPopular ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-900 hover:bg-black'}`}
          >
            Get Started
          </Button>
        </div>
        <div className="p-8 bg-white flex-grow">
          <p className="text-sm sm:text-[15px] font-medium text-gray-900 mb-3">What you get:</p>
          <ul className="space-y-3">
            <li className="flex items-start">
              <Check className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-blue-500 flex-shrink-0 mr-2 mt-0.5" />
              <span className="text-sm sm:text-[15px] text-gray-600">Never miss a customer call</span>
            </li>
            <li className="flex items-start">
              <Check className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-blue-500 flex-shrink-0 mr-2 mt-0.5" />
              <span className="text-sm sm:text-[15px] text-gray-600">100 customer calls handled for you</span>
            </li>
            <li className="flex items-start">
              <Check className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-blue-500 flex-shrink-0 mr-2 mt-0.5" />
              <span className="text-sm sm:text-[15px] text-gray-600">One business line covered</span>
            </li>
          </ul>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Refined NavLink with subtle animations
const NavLink = ({ text, href = "#" }) => {
  const navigate = useNavigate();

  const handleClick = (e) => {
    if (text === "Proof") {
      e.preventDefault();
      navigate('/compare');
    }
  };

  return (
    <motion.a 
      whileHover={{ 
        color: "#1E40AF", // Deeper blue on hover
        y: -2 
      }}
      whileTap={{ scale: 0.97 }}
      href={href}
      onClick={handleClick}
      className="text-gray-600 px-4 py-2 text-sm font-medium transition-colors duration-300 cursor-pointer"
      style={{ 
        willChange: "transform, color",
        transform: "translateZ(0)"
      }}
    >
      {text}
    </motion.a>
  );
};

// Add this component definition before the LandingPage component
const ActivityFeed = () => {
  const [activities, setActivities] = useState([]);
  const nextIndexRef = useRef(4); // Use ref instead of state
  const [isRendered, setIsRendered] = useState(false);

  // Jobs-inspired activities showing transformative moments for small business owners
  const allActivities = [
    {
      type: "call",
      icon: <Phone className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-blue-600" />,
      action: "First client call answered",
      detail: "While you had breakfast with family",
      color: "bg-blue-100"
    },
    {
      type: "message",
      icon: <MessageCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-green-600" />,
      action: "Customer question resolved",
      detail: "They just bought your premium package",
      color: "bg-green-100"
    },
    {
      type: "call",
      icon: <Phone className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-blue-600" />,
      action: "Emergency call handled",
      detail: "$3,400 project saved",
      color: "bg-blue-100"
    },
    {
      type: "calendar",
      icon: <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-amber-600" />,
      action: "Tomorrow fully booked",
      detail: "Your calendar is optimized",
      color: "bg-amber-100"
    },
    {
      type: "mail",
      icon: <Mail className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-purple-600" />,
      action: "Proposal sent at midnight",
      detail: "Beat your competition to it",
      color: "bg-purple-100"
    },
    {
      type: "crm",
      icon: <ListChecks className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-indigo-600" />,
      action: "Client returning after 8 months",
      detail: "Perfect timing follow-up worked",
      color: "bg-indigo-100"
    },
    {
      type: "message",
      icon: <MessageCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-green-600" />,
      action: "5-star review collected",
      detail: "Automatically posted to your site",
      color: "bg-green-100"
    },
    {
      type: "call",
      icon: <Phone className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-blue-600" />,
      action: "Saturday calls managed",
      detail: "Your weekend remained yours",
      color: "bg-blue-100"
    },
    {
      type: "mail",
      icon: <Mail className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-purple-600" />,
      action: "Invoice paid instantly",
      detail: "Cash flow improved by 64%",
      color: "bg-purple-100"
    },
    {
      type: "crm",
      icon: <ListChecks className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-indigo-600" />,
      action: "Hot lead qualified",
      detail: "Ready for your personal touch",
      color: "bg-indigo-100"
    },
    {
      type: "time",
      icon: <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-amber-600" />,
      action: "Your evening protected",
      detail: "Business still running perfectly",
      color: "bg-amber-100"
    },
    {
      type: "call",
      icon: <Phone className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-blue-600" />,
      action: "Urgent request captured",
      detail: "While competitors sent to voicemail",
      color: "bg-blue-100"
    }
  ];

  useEffect(() => {
    // Delay activity feed animation start
    const startDelay = setTimeout(() => {
      setIsRendered(true);
    }, 2000); // Delay activity feed until main content is rendered

    return () => clearTimeout(startDelay);
  }, []);

  useEffect(() => {
    // Only start the activity feed cycle after the initial delay
    if (!isRendered) return;

    // Initialize with first 4 activities in the exact order
    setActivities([
      {
        ...allActivities[0],
        id: 'initial-0',
      },
      {
        ...allActivities[1],
        id: 'initial-1',
      },
      {
        ...allActivities[2],
        id: 'initial-2',
      },
      {
        ...allActivities[3],
        id: 'initial-3',
      }
    ]);

    // Start with the 5th activity (index 4)
    nextIndexRef.current = 4;

    // Function to add a new activity
    const addActivity = () => {
      // Get the current index from the ref (guaranteed to be current)
      const currentIndex = nextIndexRef.current;

      // Add the new activity
      setActivities(prev => {
        const newActivity = {
          ...allActivities[currentIndex],
          id: `activity-${Date.now()}`,
        };
        return [newActivity, ...prev.slice(0, 3)];
      });

      // Update the ref for next time
      nextIndexRef.current = (currentIndex + 1) % allActivities.length;
    };

    // Add new activities at regular intervals - use a longer initial interval
    const initialDelay = setTimeout(() => {
      // Add first dynamic activity after longer delay
      addActivity();

      // Then start regular interval
      const interval = setInterval(addActivity, Math.random() * 3000 + 3500);
      return () => clearInterval(interval);
    }, 4000);

    return () => clearTimeout(initialDelay);
  }, [isRendered]);

  // Animation variants for activity items
  const itemVariants = {
    initial: { 
      opacity: 0, 
      x: -20,
    },
    animate: { 
      opacity: 1, 
      x: 0,
      transition: { 
        type: "spring",
        stiffness: 300,
        damping: 25,
        duration: 0.4,
      }
    },
    exit: { 
      opacity: 0,
      x: 20,
      transition: { 
        duration: 0.3,
        ease: [0.32, 0.72, 0, 1] // Custom easing for smoother exit
      }
    }
  };

  // Conditionally render based on screen size
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    // Check on mount
    checkMobile();

    // Add resize listener
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className="min-h-[80px] sm:min-h-[108px] relative">
      {!isRendered ? (
        // Placeholder to prevent layout shifts
        <div>
          {[0, 1, 2, isMobile ? null : 3].filter(Boolean).map((i) => (
            <div key={i} className="flex items-center text-[9px] sm:text-xs mb-2 sm:mb-2.5 opacity-0">
              <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full flex-shrink-0 mr-1.5 sm:mr-2"></div>
              <div className="border-b border-dashed border-gray-200 flex-grow pb-1.5 sm:pb-2">
                <span className="font-medium text-gray-700">Loading...</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <AnimatePresence mode="popLayout">
          {activities.map((activity, index) => {
            // Only show 3 items on mobile, 4 on desktop
            if (isMobile && index > 2) return null;

            return (
              <motion.div 
                key={activity.id} 
                layout
                variants={itemVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                className="flex items-center text-[9px] sm:text-xs mb-2 sm:mb-2.5"
                style={{ 
                  height: isMobile ? "18px" : "22px",
                  willChange: "transform, opacity" 
                }}
              >
                <motion.div 
                  className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full ${activity.color} flex-shrink-0 flex items-center justify-center mr-1.5 sm:mr-2`}
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.1 }}
                  style={{ willChange: "transform" }}
                >
                  {activity.icon}
                </motion.div>
                <div className="border-b border-dashed border-gray-200 flex-grow pb-1.5 sm:pb-2">
                  <span className="font-medium text-gray-700">{activity.action}:</span>
                  <span className="text-gray-500 ml-1">{activity.detail}</span>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      )}
    </div>
  );
};

// Mobile menu for an elegant iOS experience
const MobileMenu = ({ isOpen, onClose, onGetStartedClick }) => {
  const navigate = useNavigate();
  const menuVariants = {
    closed: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }
    },
    open: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1],
        staggerChildren: 0.05,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    closed: { 
      opacity: 0, 
      y: -8,
      transition: { duration: 0.2 }
    },
    open: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.3 }
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className="fixed inset-0 bg-white z-50 flex flex-col pt-20 px-6"
          initial="closed"
          animate="open"
          exit="closed"
          variants={menuVariants}
          style={{ 
            willChange: "transform, opacity",
            transform: "translateZ(0)",
            backfaceVisibility: "hidden"
          }}
        >
          <motion.button
            className="absolute top-7 right-6 p-1"
            onClick={onClose}
            whileTap={{ scale: 0.95 }}
            style={{ willChange: "transform" }}
          >
            <X className="w-6 h-6 text-gray-900" />
          </motion.button>

          <nav className="flex flex-col space-y-5 mt-5">
            {["Features", "How It Works", "Pricing", "Testimonials", "Proof"].map((item, i) => (
              <motion.a
                key={item}
                href={item === "Proof" ? "#" : `#${item.toLowerCase().replace(/\s+/g, '-')}`}
                className="text-lg font-medium text-gray-900 py-2 border-b border-gray-100 cursor-pointer"
                onClick={(e) => {
                  if (item === "Proof") {
                    e.preventDefault();
                    onClose();
                    navigate('/compare');
                  } else {
                    onClose();
                  }
                }}
                variants={itemVariants}
                whileTap={{ scale: 0.98 }}
                style={{ willChange: "transform, opacity" }}
              >
                {item}
              </motion.a>
            ))}
          </nav>

          <div className="mt-auto pb-10 pt-6 flex flex-col space-y-4">
            <motion.div variants={itemVariants} style={{ willChange: "transform, opacity" }}>
              <Button
                variant="outline"
                onClick={() => {
                  onClose();
                  window.location.href = '/login';
                }}
                className="w-full py-6 text-gray-900 border-gray-200"
              >
                Log in
              </Button>
            </motion.div>
            <motion.div variants={itemVariants} style={{ willChange: "transform, opacity" }}>
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700 py-6"
                onClick={onGetStartedClick}
              >
                90 Days Free
              </Button>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Main Landing Page component
const LandingPage = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const [animationsEnabled, setAnimationsEnabled] = useState(false);

  // Parallax effects with smoother motion for cinematic feel
  const heroY = useTransform(scrollYProgress, [0, 0.2], [0, -40]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0.9]);

  // Smoother scroll tracking
  const smoothScrollProgress = useSpring(scrollYProgress, { 
    stiffness: 100, 
    damping: 30, 
    restDelta: 0.001 
  });

  // Progressive animation loading strategy
  useEffect(() => {
    // Stage 1: Let the browser finish initial layout
    if (typeof window !== 'undefined') {
      // Disable all animations initially
      document.documentElement.classList.add('no-animations');

      // Stage 2: First meaningful paint optimization
      const enableAnimations = () => {
        // Give browser a brief moment to settle initial layout
        setTimeout(() => {
          // Remove the global animation block
          document.documentElement.classList.remove('no-animations');
          // Enable animations in React state
          setAnimationsEnabled(true);
        }, 100);
      };

      // Use requestIdleCallback for non-critical work
      if ('requestIdleCallback' in window) {
        requestIdleCallback(enableAnimations, { timeout: 500 });
      } else {
        setTimeout(enableAnimations, 200);
      }

      // Add CSS to block animations initially
      const style = document.createElement('style');
      style.textContent = `
        .no-animations * {
          animation-delay: 0s !important;
          animation-duration: 0s !important;
          transition-duration: 0s !important;
        }
      `;
      document.head.appendChild(style);

      return () => {
        document.head.removeChild(style);
      };
    }
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  // Handle scroll change for navbar appearance
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Update document title
  useEffect(() => {
    document.title = "Savant - The AI Assistant for Your Business";

    // Optimize image loading
    if (typeof window !== 'undefined') {
      // Add preload hints for critical resources
      const preloadHints = [
        { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
        { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' }
      ];

      preloadHints.forEach(hint => {
        const link = document.createElement('link');
        link.rel = hint.rel;
        link.href = hint.href;
        if (hint.crossOrigin) link.crossOrigin = hint.crossOrigin;
        document.head.appendChild(link);
      });
    }

    // Add performance optimization metadata
    const meta = document.createElement('meta');
    meta.name = 'viewport';
    meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0';
    document.head.appendChild(meta);

    // Add CSS animations to optimize performance
    const style = document.createElement('style');
    style.textContent = `
      /* Critical CSS to prevent layout shifts during loading */
      .critical-content {
        opacity: 1 !important;
        transform: none !important;
      }

      /* Set min heights for containers to prevent layout shifts */
      .min-h-hero {
        min-height: 200px;
      }

      /* Reduce animation workload by using hardware acceleration */
      .hardware-accelerated {
        transform: translateZ(0);
        will-change: transform, opacity;
        backface-visibility: hidden;
      }

      /* Predefine space for elements that will animate in */
      .reserve-space {
        opacity: 0;
        min-height: var(--min-h, 20px);
      }

      @keyframes fadeIn {
        from { opacity: 0; transform: translateX(-5px); }
        to { opacity: 1; transform: translateX(0); }
      }

      @keyframes growWidth {
        from { width: 0%; }
        to { width: 70%; }
      }

      @keyframes growWidth90 {
        from { width: 0%; }
        to { width: 90%; }
      }

      @keyframes growWidth94 {
        from { width: 0%; }
        to { width: 94%; }
      }

      @keyframes pulse {
        0% { opacity: 0.8; }
        50% { opacity: 1; }
        100% { opacity: 0.8; }
      }

      @keyframes rotateCircle {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }

      @keyframes breathe {
        0% { transform: scale(1); }
        50% { transform: scale(1.03); }
        100% { transform: scale(1); }
      }

      @keyframes typewriter {
        from { width: 0; }
        to { width: 100%; }
      }

      @keyframes blink {
        0%, 100% { opacity: 1; }
        50% { opacity: 0; }
      }

      @keyframes fadeInUp {
        from { 
          opacity: 0; 
          transform: translateY(8px);
        }
        to { 
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes slideIn {
        from { 
          opacity: 0; 
          transform: translateX(-10px);
        }
        to { 
          opacity: 1;
          transform: translateX(0);
        }
      }

      .animate-fadeIn {
        animation: fadeIn 0.3s ease forwards;
      }

      .animate-fadeInUp {
        animation: fadeInUp 0.4s cubic-bezier(0.22, 1, 0.36, 1) forwards;
      }

      .animate-slideIn {
        animation: slideIn 0.35s cubic-bezier(0.22, 1, 0.36, 1) forwards;
      }

      .animate-growWidth {
        animation: growWidth 1.2s ease-out forwards;
      }

      .animate-growWidth90 {
        animation: growWidth90 1.2s ease-out forwards;
      }

      .animate-growWidth94 {
        animation: growWidth94 1.2s ease-out forwards;
      }

      .animate-pulse-subtle {
        animation: pulse 3s infinite ease-in-out;
      }

      .animate-rotate {
        animation: rotateCircle 12s linear infinite;
      }

      .animate-breathe {
        animation: breathe 5s infinite ease-in-out;
      }

      .animate-typewriter {
        animation: typewriter 3s steps(40, end) forwards;
        white-space: nowrap;
        overflow: hidden;
      }

      .animate-cursor {
        animation: blink 0.7s infinite;
      }

      @keyframes ping {
        0% {
          transform: scale(1);
          opacity: 0.75;
        }
        70% {
          transform: scale(2);
          opacity: 0;
        }
        100% {
          transform: scale(2.5);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const handleGetStarted = () => {
    navigate('/signup');
  };

  // Refined animation variants for staggered elements
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.12,
        delayChildren: 0.2,
        ease: [0.22, 1, 0.36, 1],
        duration: 0.9
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        type: "tween",
        duration: 0.9, 
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  // Button animation variants
  const buttonVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring", 
        stiffness: 300,
        damping: 25,
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    },
    hover: {
      scale: 1.05,
      transition: { 
        type: "spring", 
        stiffness: 300, 
        damping: 25 
      }
    },
    tap: {
      scale: 0.95,
      transition: { 
        type: "spring", 
        stiffness: 300, 
        damping: 15 
      }
    }
  };

  useEffect(() => {
    // Live time update
    const updateTime = () => {
      const timeDisplay = document.getElementById('timeDisplay');
      if (timeDisplay) {
        const now = new Date();
        const hours = now.getHours();
        const minutes = now.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const formattedHours = hours % 12 || 12;
        const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
        timeDisplay.textContent = `${formattedHours}:${formattedMinutes} ${ampm}`;
      }
    };

    // Update time immediately and then every second
    updateTime();
    const interval = setInterval(updateTime, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Refined navbar with subtle glass effect when scrolled */}
      <motion.header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? 'bg-white/90 backdrop-blur-md shadow-sm' : 'bg-transparent'
        }`}
        animate={{ height: isScrolled ? (window.innerWidth < 640 ? 60 : 68) : (window.innerWidth < 640 ? 72 : 84) }}
        style={{ 
          willChange: "height, background",
          transform: "translateZ(0)",
          minHeight: window.innerWidth < 640 ? 60 : 68,
          backfaceVisibility: "hidden"
        }}
      >
        <nav className="max-w-6xl mx-auto px-4 sm:px-6 h-full flex items-center justify-between">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={animationsEnabled ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
            transition={{ 
              duration: 0.6, 
              delay: 0.2,
              ease: [0.22, 1, 0.36, 1] // Custom cubic-bezier for smoother animation
            }}
            className="flex items-center"
            style={{ willChange: "transform, opacity" }}
          >
            <div className="relative">
              <span className="font-medium text-lg text-gray-900" style={{ letterSpacing: '-0.02em' }}>savant</span>
              <div 
                className="absolute top-0.5 -right-1.5 bg-blue-600" 
                style={{ 
                  width: '4px', 
                  height: '4px', 
                  borderRadius: '50%',
                  transform: 'scale(1.2)',
                  transformOrigin: 'center'
                }}
              ></div>
            </div>
          </motion.div>

          <div className="hidden md:flex items-center space-x-2">
            {["Features", "How It Works", "Pricing", "Testimonials", "Proof"].map((item, index) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, y: -10 }}
                animate={animationsEnabled ? { opacity: 1, y: 0 } : { opacity: 0, y: -10 }}
                transition={{ 
                  duration: 0.6, 
                  delay: animationsEnabled ? (0.25 + index * 0.07) : 0, // Staggered delay for each link
                  ease: [0.22, 1, 0.36, 1] 
                }}
                style={{ willChange: "transform, opacity" }}
              >
                <NavLink text={item} href={`#${item.toLowerCase().replace(/\s+/g, '-')}`} />
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="hidden md:flex items-center space-x-4"
            initial={{ opacity: 0, x: 20 }}
            animate={animationsEnabled ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }}
            transition={{ 
              duration: 0.6, 
              delay: 0.3,
              ease: [0.22, 1, 0.36, 1]
            }}
            style={{ willChange: "transform, opacity" }}
          >
            <Button
              variant="ghost"
              onClick={() => navigate('/login')}
              className="text-sm"
            >
              Log in
            </Button>
            <Button
              onClick={handleGetStarted}
              className="bg-blue-600 hover:bg-blue-700 text-sm"
            >
              90 Days Free
            </Button>
          </motion.div>

          {/* Mobile menu button */}
          <motion.button
            className="md:hidden flex items-center justify-center"
            onClick={() => setMenuOpen(true)}
            initial={{ opacity: 0, x: 20 }}
            animate={animationsEnabled ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }}
            transition={{ 
              duration: 0.6, 
              delay: 0.3,
              ease: [0.22, 1, 0.36, 1]
            }}
            whileTap={{ scale: 0.95 }}
            style={{ willChange: "transform, opacity" }}
          >
            <Menu className="w-6 h-6 text-gray-900" />
          </motion.button>
        </nav>
      </motion.header>

      {/* Mobile menu */}
      <MobileMenu 
        isOpen={menuOpen} 
        onClose={() => setMenuOpen(false)} 
        onGetStartedClick={handleGetStarted} 
      />

      {/* Hero Section with enhanced visual design */}
      <section className="pt-28 sm:pt-32 pb-16 sm:pb-20 md:pt-40 overflow-hidden relative">
        {/* Abstract background elements */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <div className="absolute -inset-[10%] bg-gradient-to-r from-blue-50 to-indigo-50 opacity-50 blur-3xl" />
          <motion.div 
            className="absolute right-0 sm:right-10 top-40 w-64 sm:w-80 h-64 sm:h-80 bg-blue-100 rounded-full opacity-30 blur-3xl"
            animate={{ 
              x: [0, 15, 0],
              y: [0, 20, 0],
            }}
            transition={{ 
              duration: 25,
              repeat: Infinity,
              repeatType: "reverse",
              delay: 2 // Delay background animations until content is loaded
            }}
            style={{ willChange: "transform" }}
          />
          <motion.div 
            className="absolute left-1/4 sm:left-1/3 bottom-0 w-48 sm:w-64 h-48 sm:h-64 bg-indigo-50 rounded-full opacity-30 blur-3xl"
            animate={{ 
              x: [0, -30, 0],
              y: [0, -15, 0],
            }}
            transition={{ 
              duration: 20,
              repeat: Infinity,
              repeatType: "reverse",
              delay: 2.5 // Stagger background animations
            }}
            style={{ willChange: "transform" }}
          />
        </div>

        <motion.div 
          className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10"
          style={{ 
            y: heroY, 
            opacity: heroOpacity,
            willChange: "transform, opacity" 
          }}
        >
          <motion.div 
            className="max-w-3xl mx-auto text-center mb-12 sm:mb-16 md:mb-24"
            variants={containerVariants}
            initial="hidden"
            animate={animationsEnabled ? "visible" : "hidden"}
            style={{ willChange: "opacity" }}
          >
            <motion.h1 
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 sm:mb-8 tracking-tight leading-tight min-h-[40px] sm:min-h-[50px] md:min-h-[60px] lg:min-h-[72px]"
              variants={itemVariants}
              style={{ willChange: "transform, opacity" }}
            >
              The Business Assistant That Just Works
            </motion.h1>
            <motion.p 
              className="text-lg sm:text-xl text-gray-600 mb-3 leading-relaxed px-1 min-h-[28px] sm:min-h-[30px]"
              variants={itemVariants}
              style={{ willChange: "transform, opacity" }}
            >
              Imagine your business running itself while you focus on what truly matters.
            </motion.p>
            <motion.p 
              className="text-lg sm:text-xl text-gray-600 mb-8 sm:mb-12 leading-relaxed px-1 min-h-[28px] sm:min-h-[30px]"
              variants={itemVariants}
              style={{ willChange: "transform, opacity" }}
            >
              No complexity. No learning curve. Pure magic.
            </motion.p>
            <motion.div 
              className="flex flex-col items-center"
              variants={itemVariants}
              style={{ willChange: "transform, opacity" }}
            >
              <motion.div
                className="relative w-full sm:w-auto"
                whileHover={{ 
                  scale: 1.03,
                  transition: { 
                    type: "spring", 
                    stiffness: 300, 
                    damping: 25 
                  }
                }}
                whileTap={{ 
                  scale: 0.97,
                  transition: { 
                    type: "spring", 
                    stiffness: 300, 
                    damping: 15 
                  }
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ 
                  opacity: 1, 
                  y: 0,
                  transition: {
                    opacity: { duration: 1.2, ease: [0.19, 1, 0.22, 1] },
                    y: { 
                      duration: 1.8, 
                      ease: [0.19, 1, 0.22, 1] 
                    },
                    delay: 0.8
                  }
                }}
              >
                <Button 
                  onClick={handleGetStarted}
                  size="lg" 
                  className="relative bg-blue-600 hover:bg-blue-700 px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg w-full sm:w-auto overflow-hidden group"
                >
                  <motion.span 
                    className="relative z-10 flex items-center justify-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ 
                      duration: 1.0, 
                      delay: 1.6,
                      ease: [0.19, 1, 0.22, 1]
                    }}
                  >
                    Experience the Magic
                    <motion.div
                      className="ml-2 w-5 h-5"
                      animate={{ 
                        x: [0, 4, 0],
                      }}
                      transition={{ 
                        duration: 2.5, 
                        ease: "easeInOut", 
                        repeat: Infinity,
                        repeatType: "reverse",
                        delay: 2.2
                      }}
                    >
                      <ArrowRight className="w-5 h-5" />
                    </motion.div>
                  </motion.span>
                  <motion.div 
                    className="absolute inset-0 bg-blue-500 opacity-0 group-hover:opacity-20"
                    initial={{ scale: 0, opacity: 0 }}
                    whileHover={{ 
                      scale: 1.5, 
                      opacity: 0.15,
                      transition: { duration: 0.6 } 
                    }}
                  />
                </Button>

                {/* Primary full-width sky blue glow */}
                <motion.div
                  className="absolute inset-0 rounded-lg -z-10"
                  initial={{ opacity: 0.2 }}
                  animate={{ opacity: 0.3 }}
                  whileHover={{ opacity: 0.85 }}
                  transition={{ duration: 0.5 }}
                  style={{
                    background: "radial-gradient(ellipse 80% 50% at center, rgba(37, 99, 235, 0.3) 0%, rgba(37, 99, 235, 0.4) 30%, rgba(37, 99, 235, 0) 80%)",
                    filter: "blur(15px)",
                    transform: "scale(1.2, 1.15)"
                  }}
                />

                {/* Horizontal glow to ensure full button coverage */}
                <motion.div
                  className="absolute inset-0 -z-20 rounded-lg"
                  initial={{ opacity: 0.15 }}
                  animate={{ opacity: 0.2 }}
                  whileHover={{ opacity: 0.6 }}
                  transition={{ duration: 0.5 }}
                  style={{
                    background: "linear-gradient(90deg, rgba(37, 99, 235, 0) 0%, rgba(37, 99, 235, 0.6) 20%, rgba(37, 99, 235, 0.6) 80%, rgba(37, 99, 235, 0) 100%)",
                    filter: "blur(18px)",
                    transform: "scale(1.4, 1.15)"
                  }}
                />

                {/* Ambient pulsing glow animation - wider coverage */}
                <motion.div
                  className="absolute inset-0 -z-30 rounded-lg"
                  animate={{ 
                    opacity: [0.05, 0.2, 0.05],
                    scale: [1.1, 1.25, 1.1]
                  }}
                  transition={{
                    duration: 4,
                    ease: "easeInOut",
                    repeat: Infinity,
                    repeatType: "loop"
                  }}
                  style={{
                    background: "radial-gradient(ellipse 100% 50% at center, rgba(37, 99, 235, 0.7) 0%, rgba(37, 99, 235, 0.4) 50%, rgba(37, 99, 235, 0) 100%)",
                    filter: "blur(20px)"
                  }}
                />
              </motion.div>
              <span className="text-xs text-gray-500 mt-2">90 Days Free</span>


            </motion.div>
          </motion.div>

          {/* Redesigned cinematic UI mockup inspired by Christopher Nolan's visual style - optimized for mobile */}
          <motion.div
            className="relative mx-auto max-w-[345px] sm:max-w-xl md:max-w-2xl lg:max-w-4xl"
            initial={{ opacity: 0 }}
            animate={animationsEnabled ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            style={{ 
              minHeight: "300px",
              willChange: "opacity" 
            }}
          >
            {/* Vignette effect that adds depth and drama */}
            <div className="absolute inset-0 rounded-xl bg-gradient-radial from-transparent to-black/20 z-10 pointer-events-none" />

            {/* Main device frame with dramatic shadow */}
            <motion.div 
              className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200 relative z-0"
              initial={{ y: 60, scale: 0.95, opacity: 0 }}
              animate={animationsEnabled ? { y: 0, scale: 1, opacity: 1 } : { y: 60, scale: 0.95, opacity: 0 }}
              transition={{ 
                duration: 1.5, 
                delay: 1.0, // Increased delay to let page content load first
                ease: [0.19, 1, 0.22, 1] // Custom easing for cinematic feel
              }}
              style={{ 
                willChange: "transform, opacity",
                height: "auto",
                minHeight: "300px"
              }}
            >
              {/* Device top bar */}
              <div className="bg-gray-900 h-5 sm:h-7 flex items-center px-3 sm:px-4">
                <div className="flex space-x-1 sm:space-x-1.5">
                  <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-red-500"></div>
                  <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-yellow-500"></div>
                  <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-green-500"></div>
                </div>
              </div>

              {/* Main content area with "time layers" concept inspired by Nolan */}
              <div className="bg-gray-50 p-3 sm:p-4 md:p-6 relative">
                {/* Layer 1: Always-on command center - conceptual top view */}
                <motion.div
                  className="bg-white rounded-lg shadow-sm border border-gray-100 p-3 sm:p-4 md:p-5 relative z-20"
                  initial={{ opacity: 0, y: 20 }}
                  animate={animationsEnabled ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ 
                    delay: 1.5, // Increased delay for staggered animations
                    duration: 0.8, 
                    ease: "easeOut"
                  }}
                  style={{
                    willChange: "opacity, transform",
                    minHeight: "150px"
                  }}
                >
                  <div className="flex items-center justify-between mb-3 sm:mb-5">
                    <div className="flex items-center">
                      <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-lg bg-blue-50 flex items-center justify-center mr-2 sm:mr-4 relative">
                        <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-sm sm:text-base text-gray-900">Your Business. Automated.</h3>
                        <div className="hidden sm:flex items-center text-xs text-gray-500">
                          <Clock className="w-3 h-3 mr-1.5" />
                          <span>Working while you live</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Effortless
                      </span>
                    </div>
                  </div>

                  {/* Channel control - showcasing the multiple channels Savant handles */}
                  <div className="mb-3 sm:mb-5 bg-gray-50 rounded-lg p-2 sm:p-3 border border-gray-100">
                    <div className="flex items-center justify-between mb-1 sm:mb-2">
                      <h4 className="text-[10px] sm:text-xs font-medium text-gray-700">Everything. Handled.</h4>
                    </div>
                    <div className="flex space-x-1 sm:space-x-1.5 mb-1 sm:mb-3">
                      <div className="flex-1 py-1 sm:py-1.5 px-1 sm:px-3 bg-blue-50 rounded text-center text-[9px] sm:text-xs font-medium text-blue-700 border border-blue-100">
                        <Phone className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 mx-auto mb-0.5 sm:mb-1" />
                        <span className="hidden sm:inline">No Missed Calls</span>
                        <span className="inline sm:hidden">Calls</span>
                      </div>
                      <div className="flex-1 py-1 sm:py-1.5 px-1 sm:px-3 bg-green-50 rounded text-center text-[9px] sm:text-xs font-medium text-green-700 border border-green-100">
                        <MessageCircle className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 mx-auto mb-0.5 sm:mb-1" />
                        <span className="hidden sm:inline">Instant Replies</span>
                        <span className="inline sm:hidden">Replies</span>
                      </div>
                      <div className="flex-1 py-1 sm:py-1.5 px-1 sm:px-3 bg-purple-50 rounded text-center text-[9px] sm:text-xs font-medium text-purple-700 border border-purple-100">
                        <Mail className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 mx-auto mb-0.5 sm:mb-1" />
                        <span className="hidden sm:inline">Zero Inbox</span>
                        <span className="inline sm:hidden">Inbox</span>
                      </div>
                      <div className="flex-1 py-1 sm:py-1.5 px-1 sm:px-3 bg-amber-50 rounded text-center text-[9px] sm:text-xs font-medium text-amber-700 border border-amber-100">
                        <Globe className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 mx-auto mb-0.5 sm:mb-1" />
                        <span className="hidden sm:inline">Full Calendar</span>
                        <span className="inline sm:hidden">Calendar</span>
                      </div>
                    </div>
                  </div>

                  {/* Live Activity Feed - enhanced with more dynamic content */}
                  <div className="bg-gray-50 rounded-lg border border-gray-100 overflow-hidden">
                    <div className="bg-gray-100 py-1 sm:py-1.5 px-2 sm:px-3 flex items-center justify-between">
                      <h4 className="text-[10px] sm:text-xs font-medium text-gray-700 flex items-center">
                        While You Were Away
                      </h4>
                      <div className="text-[10px] sm:text-xs text-gray-500 flex items-center">
                        <div className="relative w-1.5 h-1.5 mr-1.5">
                          <div 
                            className="absolute top-0 left-0 w-1.5 h-1.5 rounded-full bg-green-500 z-10"
                          ></div>
                          <div 
                            className="absolute top-0 left-0 w-1.5 h-1.5 rounded-full bg-green-500 opacity-75"
                            style={{ 
                              animation: 'ping 2s cubic-bezier(0, 0, 0.2, 1) infinite',
                            }}
                          ></div>
                        </div>
                        <span>Happening Now</span>
                      </div>
                    </div>
                    <div className="p-2 sm:p-3 overflow-hidden relative">
                      {/* Activity feed container */}
                      <ActivityFeed />
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* Cinematic floating elements that illustrate the AI's impact - optimized for mobile */}
            <motion.div 
              className="absolute -top-3 sm:-top-4 -right-2 sm:-right-4 bg-white rounded-lg shadow-lg p-2 sm:p-3 border border-gray-100 z-20"
              initial={{ opacity: 0, scale: 0.8, rotate: 5 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ delay: 2.8, duration: 0.7, type: "spring" }}
            >
              <div className="flex items-center space-x-1 sm:space-x-2">
                <div className="w-5 h-5 sm:w-7 sm:h-7 bg-green-100 rounded-md flex items-center justify-center">
                  <Check className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 text-green-600" />
                </div>
                <div className="text-[10px] sm:text-xs font-medium">87 Tasks Completed Today</div>
              </div>
            </motion.div>

            <motion.div 
              className="absolute -bottom-3 sm:-bottom-5 -left-2 sm:-left-6 bg-white rounded-lg shadow-lg p-2 sm:p-3 border border-gray-100 z-20"
              initial={{ opacity: 0, scale: 0.8, rotate: -5 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ delay: 3.0, duration: 0.7, type: "spring" }}
            >
              <div className="flex items-center space-x-1 sm:space-x-2">
                <div className="w-5 h-5 sm:w-7 sm:h-7 bg-blue-100 rounded-md flex items-center justify-center">
                  <Clock className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 text-blue-600" />
                </div>
                <div className="text-[10px] sm:text-xs font-medium">Working 24/7 for You</div>
              </div>
            </motion.div>

            {/* Subtle reflection effect for that premium Nolan-esque finish */}
            <div className="absolute bottom-0 left-0 right-0 h-12 sm:h-16 bg-gradient-to-t from-white/30 to-transparent blur-sm -mb-6 sm:-mb-8 z-0"></div>
          </motion.div>
        </motion.div>
      </section>

      {/* Features Section - Jobs-inspired simplicity */}
      <section id="features" className="py-16 sm:py-20 md:py-24 px-4 sm:px-6">
        <div className="max-w-[1120px] mx-auto">
          <AnimatedSection className="text-center mb-10 sm:mb-14 md:mb-20">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4 tracking-tight">
              Your Business. Reinvented.
            </h2>
            <p className="max-w-xl mx-auto text-base sm:text-[17px] leading-relaxed text-gray-600 px-1">
              Run your business without running yourself into the ground.
            </p>
          </AnimatedSection>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            <FeatureCard 
              icon={Clock}
              title="Your Time Back"
              description="Close your laptop at 5PM. Take weekends off. Savant never stops working, even when you do."
              delay={0.1}
            />
            <FeatureCard 
              icon={MessageCircle}
              title="Every Call Answered"
              description="No more missed opportunities. Your customers get instant help while you focus on growing your business."
              delay={0.2}
            />
            <FeatureCard 
              icon={Brain}
              title="Gets Smarter Daily"
              description="The more you use it, the better it gets. Like having an employee who never forgets and always improves."
              delay={0.3}
            />
            <FeatureCard 
              icon={Sparkles}
              title="Big-Business Service"
              description="Give your customers the white-glove treatment they deserve, without hiring a receptionist or call center."
              delay={0.4}
            />
            <FeatureCard 
              icon={Globe}
              title="Works With What You Have"
              description="No new phone systems. No new software to learn. Savant connects to what you already use, instantly."
              delay={0.5}
            />
            <FeatureCard 
              icon={Zap}
              title="Grow Without Hiring"
              description="Scale your business without scaling your team. Handle twice the customers without doubling your stress."
              delay={0.6}
            />
          </div>
        </div>
      </section>

      {/* Real Calls Section - The proof is in the experience */}
      <section id="real-calls" className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 bg-white overflow-hidden">
        <div className="max-w-[1040px] mx-auto">
          <AnimatedSection className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 tracking-tight">
              Every Call Answered
            </h2>
            <p className="max-w-xl mx-auto text-base sm:text-lg leading-relaxed text-gray-600 px-1">
              Listen to actual customer calls handled exactly as you would. No missed business, no interruptions.
            </p>
          </AnimatedSection>

          {/* Audio Showcase - Jobs-inspired simplicity */}
          <AudioShowcase onGetStarted={handleGetStarted} />

          {/* Link to competitor comparison */}
          <AnimatedSection className="text-center mt-8 sm:mt-12" delay={0.6}>
            <button
              onClick={() => navigate('/compare')}
              className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors duration-200 group"
            >
              <span>Compare With Our Biggest Competitor</span>
              <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-200 group-hover:translate-x-1" />
            </button>
          </AnimatedSection>
        </div>
      </section>

      {/* How It Works Section - Jobs-inspired clarity */}
      <section id="how-it-works" className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <AnimatedSection className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 tracking-tight">
              Ridiculously Simple
            </h2>
            <p className="max-w-md mx-auto text-base sm:text-[17px] leading-relaxed text-gray-600 px-1">
              No manuals. No training. No consultants. Just three steps to freedom.
            </p>
          </AnimatedSection>

          <div className="space-y-12 sm:space-y-16">
            {/* Step 1 */}
            <AnimatedSection>
              <div className="grid md:grid-cols-5 gap-6 sm:gap-8 items-center">
                <div className="md:col-span-2">
                  <div className="aspect-square w-[140px] sm:w-[180px] md:w-full max-w-[240px] mx-auto bg-blue-50 rounded-full flex items-center justify-center">
                    <span className="text-5xl sm:text-6xl font-light text-blue-600">1</span>
                  </div>
                </div>
                <div className="md:col-span-3 text-center md:text-left">
                  <h3 className="text-xl sm:text-2xl font-medium mb-2 sm:mb-3 text-gray-900 tracking-tight">Have a 10-minute chat</h3>
                  <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                    "This is how I answer the phone." "Here's what we sell." That's it. 
                    Savant learns your business in minutes, not months.
                  </p>
                </div>
              </div>
            </AnimatedSection>

            {/* Minimal divider */}
            <div className="w-12 sm:w-16 h-[1px] bg-gray-200 mx-auto"></div>

            {/* Step 2 */}
            <AnimatedSection>
              <div className="grid md:grid-cols-5 gap-6 sm:gap-8 items-center">
                <div className="md:col-span-3 md:order-1 order-2 text-center md:text-left">
                  <h3 className="text-xl sm:text-2xl font-medium mb-2 sm:mb-3 text-gray-900 tracking-tight">Plug in your tools</h3>
                  <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                    One click to connect your phone. Another for your calendar. That's it.  
                    No IT department required.
                  </p>
                </div>
                <div className="md:col-span-2 order-1 md:order-2">
                  <div className="aspect-square w-[140px] sm:w-[180px] md:w-full max-w-[240px] mx-auto bg-blue-50 rounded-full flex items-center justify-center">
                    <span className="text-5xl sm:text-6xl font-light text-blue-600">2</span>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Minimal divider */}
            <div className="w-12 sm:w-16 h-[1px] bg-gray-200 mx-auto"></div>

            {/* Step 3 */}
            <AnimatedSection>
              <div className="grid md:grid-cols-5 gap-6 sm:gap-8 items-center">
                <div className="md:col-span-2">
                  <div className="aspect-square w-[140px] sm:w-[180px] md:w-full max-w-[240px] mx-auto bg-blue-50 rounded-full flex items-center justify-center">
                    <span className="text-5xl sm:text-6xl font-light text-blue-600">3</span>
                  </div>
                </div>
                <div className="md:col-span-3 text-center md:text-left">
                  <h3 className="text-xl sm:text-2xl font-medium mb-2 sm:mb-3 text-gray-900 tracking-tight">Go home earlier</h3>
                  <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                    While you sleep, Savant works. While you vacation, Savant works. 
                    Your business never stops, but you finally can.
                  </p>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Jobs-inspired authenticity */}
      <section id="testimonials" className="py-16 sm:py-20 md:py-24 px-4 sm:px-6">
        <div className="max-w-[1120px] mx-auto">
          <AnimatedSection className="text-center mb-10 sm:mb-14 md:mb-20">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 tracking-tight">
              People Like You
            </h2>
            <p className="max-w-xl mx-auto text-base sm:text-[17px] leading-relaxed text-gray-600 px-1">
              Small business owners who got their lives back
            </p>
          </AnimatedSection>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-6 md:gap-8">
            <AnimatedSection delay={0.1}>
              <div className="bg-white rounded-xl p-5 sm:p-7 md:p-8 shadow-[0_2px_10px_-3px_rgba(0,0,0,0.04)] border border-gray-100 h-full flex flex-col">
                <div className="mb-5 sm:mb-6">
                  <p className="text-sm sm:text-[15px] text-gray-700 leading-relaxed mb-4 sm:mb-5">
                    <span className="font-medium text-gray-900">"I've gotten 3 hours back every day.</span> No more staying late to return calls. No more losing customers because we missed them. No more explaining our hours for the tenth time today."
                  </p>
                  <p className="text-sm sm:text-[15px] text-gray-600 italic">
                    "Last month I made it to my son's baseball games. All of them."
                  </p>
                </div>
                <div className="mt-auto pt-4 border-t border-gray-100">
                  <div className="font-medium text-gray-900 text-sm sm:text-[15px]">Carlos Rodriguez</div>
                  <div className="text-gray-500 text-xs sm:text-[13px]">Rodriguez Auto Repair, 4 employees</div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="bg-white rounded-xl p-5 sm:p-7 md:p-8 shadow-[0_2px_10px_-3px_rgba(0,0,0,0.04)] border border-gray-100 h-full flex flex-col">
                <div className="mb-5 sm:mb-6">
                  <p className="text-sm sm:text-[15px] text-gray-700 leading-relaxed mb-4 sm:mb-5">
                    <span className="font-medium text-gray-900">"We grew 43% last year with the same staff.</span> Savant handles our appointment scheduling, sends reminders, and answers common questions about our services. My receptionist now manages our social media."
                  </p>
                  <p className="text-sm sm:text-[15px] text-gray-600 italic">
                    "I finally took a two-week vacation. First one in 7 years."
                  </p>
                </div>
                <div className="mt-auto pt-4 border-t border-gray-100">
                  <div className="font-medium text-gray-900 text-sm sm:text-[15px]">Lisa Chen</div>
                  <div className="text-gray-500 text-xs sm:text-[13px]">Bright Smile Dental, 8 employees</div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.3} className="sm:col-span-2 md:col-span-1">
              <div className="bg-white rounded-xl p-5 sm:p-7 md:p-8 shadow-[0_2px_10px_-3px_rgba(0,0,0,0.04)] border border-gray-100 h-full flex flex-col">
                <div className="mb-5 sm:mb-6">
                  <p className="text-sm sm:text-[15px] text-gray-700 leading-relaxed mb-4 sm:mb-5">
                    <span className="font-medium text-gray-900">"I was skeptical about AI. Now I can't imagine life without it.</span> Setup was so easy. I didn't even need to hire the neighbor's kid to help. Just talked about my business for 10 minutes."
                  </p>
                  <p className="text-sm sm:text-[15px] text-gray-600 italic">
                    "We get compliments on our 'amazing customer service' almost daily."
                  </p>
                </div>
                <div className="mt-auto pt-4 border-t border-gray-100">
                  <div className="font-medium text-gray-900 text-sm sm:text-[15px]">David Wilson</div>
                  <div className="text-gray-500 text-xs sm:text-[13px]">Wilson Home Services, Solo entrepreneur</div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Pricing Section - Jobs-inspired simplicity */}
      <section id="pricing" className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-[1120px] mx-auto">
          <AnimatedSection className="text-center mb-10 sm:mb-14 md:mb-20">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 tracking-tight">
              Worth Every Penny
            </h2>
            <p className="max-w-xl mx-auto text-base sm:text-[17px] leading-relaxed text-gray-600 px-1">
              Costs less than a part-time employee. Does more than a full-time team.
            </p>
          </AnimatedSection>

          {/* Pricing cards with mobile optimization - show one at a time on very small screens */}
          <div className="grid md:grid-cols-3 gap-6 md:gap-8 max-w-4xl mx-auto">
            <AnimatedSection delay={0.1}>
              <div className="bg-white rounded-xl overflow-hidden h-full flex flex-col shadow-[0_2px_10px_-3px_rgba(0,0,0,0.04)] border border-gray-100 transition-all duration-500 hover:shadow-[0_16px_30px_-12px_rgba(0,0,0,0.08)]">
                <div className="p-5 sm:p-7 md:p-8 border-b border-gray-100">
                  <div className="flex items-center justify-between mb-1 sm:mb-2">
                    <h3 className="text-base sm:text-[17px] font-medium text-gray-900 tracking-tight">
                      Starting<br/>Out
                    </h3>
                    <div className="text-xs font-medium text-blue-600 bg-blue-50 px-2 sm:px-2.5 py-0.5 rounded-full whitespace-nowrap">
                      For solopreneurs
                    </div>
                  </div>
                  <div className="mt-4 sm:mt-5 mb-4 sm:mb-5">
                    <span className="text-2xl sm:text-3xl font-bold text-gray-900">$97</span>
                    <span className="text-gray-500 text-sm">/month</span>
                  </div>
                  <p className="text-sm sm:text-[15px] text-gray-600 mb-4 sm:mb-5">Perfect for the business owner doing it all. Take back your evenings and weekends.</p>
                  <Button 
                    className="w-full bg-gray-900 hover:bg-gray-800 hover:shadow-sm py-2 sm:py-3"
                  >
                    Get Savant Today
                  </Button>
                </div>
                <div className="p-5 sm:p-7 md:p-8 bg-white flex-grow">
                  <p className="text-sm sm:text-[15px] font-medium text-gray-900 mb-3">What you get:</p>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <Check className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-blue-500 flex-shrink-0 mr-2 mt-0.5" />
                      <span className="text-sm sm:text-[15px] text-gray-600">Never miss a customer call</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-blue-500 flex-shrink-0 mr-2 mt-0.5" />
                      <span className="text-sm sm:text-[15px] text-gray-600">100 customer calls handled for you</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-blue-500 flex-shrink-0 mr-2 mt-0.5" />
                      <span className="text-sm sm:text-[15px] text-gray-600">One business line covered</span>
                    </li>
                  </ul>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="bg-white rounded-xl overflow-hidden h-full flex flex-col shadow-[0_8px_20px_-6px_rgba(0,0,0,0.1)] border-2 border-blue-400 relative z-10 md:scale-[1.02] md:-translate-y-1 transition-all duration-500 hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.15)]">
                <div className="absolute top-0 right-0">
                  <div className="bg-blue-600 text-white text-xs font-medium px-2 py-0.5 rounded-bl-lg">
                    Most Popular
                  </div>
                </div>
                <div className="p-5 sm:p-7 md:p-8 border-b border-gray-100 bg-gradient-to-br from-blue-50 to-white">
                  <div className="flex items-center justify-between mb-1 sm:mb-2">
                    <h3 className="text-base sm:text-[17px] font-medium text-gray-900 tracking-tight">Growing Business</h3>
                    <div className="text-xs font-medium text-blue-600 bg-white px-2 sm:px-2.5 py-0.5 rounded-full whitespace-nowrap">
                      2-10 employees
                    </div>
                  </div>
                  <div className="mt-4 sm:mt-5 mb-4 sm:mb-5">
                    <span className="text-2xl sm:text-3xl font-bold text-gray-900">$197</span>
                    <span className="text-gray-500 text-sm">/month</span>
                  </div>
                  <p className="text-sm sm:text-[15px] text-gray-600 mb-4 sm:mb-5">Free your team to focus on growth. Get the equivalent of 2 full-time employees.</p>
                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700 shadow-sm py-2 sm:py-3"
                  >
                    Get Savant Today
                  </Button>
                </div>
                <div className="p-5 sm:p-7 md:p-8 bg-white flex-grow">
                  <p className="text-sm sm:text-[15px] font-medium text-gray-900 mb-3">Everything in Starting Out, plus:</p>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <Check className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-blue-500 flex-shrink-0 mr-2 mt-0.5" />
                      <span className="text-sm sm:text-[15px] text-gray-600">Scales with your business</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-blue-500 flex-shrink-0 mr-2 mt-0.5" />
                      <span className="text-sm sm:text-[15px] text-gray-600">Text your customers, without touching your phone</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-blue-500 flex-shrink-0 mr-2 mt-0.5" />
                      <span className="text-sm sm:text-[15px] text-gray-600">Calendar & CRM integration</span>
                    </li>
                  </ul>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.3}>
              <div className="bg-white rounded-xl overflow-hidden h-full flex flex-col shadow-[0_2px_10px_-3px_rgba(0,0,0,0.04)] border border-gray-100 transition-all duration-500 hover:shadow-[0_16px_30px_-12px_rgba(0,0,0,0.08)]">
                <div className="p-5 sm:p-7 md:p-8 border-b border-gray-100">
                  <div className="flex items-center justify-between mb-1 sm:mb-2">
                    <h3 className="text-base sm:text-[17px] font-medium text-gray-900 tracking-tight">Established</h3>
                    <div className="text-xs font-medium text-blue-600 bg-blue-50 px-2 sm:px-2.5 py-0.5 rounded-full whitespace-nowrap">
                      10+ employees
                    </div>
                  </div>
                  <div className="mt-4 sm:mt-5 mb-4 sm:mb-5">
                    <span className="text-xl sm:text-2xl font-bold text-gray-900">Custom</span>
                    <span className="text-xl sm:text-2xl font-bold text-gray-900"> Pricing</span>
                  </div>
                  <p className="text-sm sm:text-[15px] text-gray-600 mb-4 sm:mb-5">Coordinate multiple locations and complex customer needs with ease.</p>
                  <Button 
                    className="w-full bg-gray-900 hover:bg-gray-800 hover:shadow-sm py-2 sm:py-3"
                    onClick={() => window.location.href = 'mailto:caleb@thesavants.ai'}
                  >
                    Contact Sales
                  </Button>
                </div>
                <div className="p-5 sm:p-7 md:p-8 bg-white flex-grow">
                  <p className="text-sm sm:text-[15px] font-medium text-gray-900 mb-3">Everything in Established, plus:</p>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <Check className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-blue-500 flex-shrink-0 mr-2 mt-0.5" />
                      <span className="text-sm sm:text-[15px] text-gray-600">Works across multiple locations</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-blue-500 flex-shrink-0 mr-2 mt-0.5" />
                      <span className="text-sm sm:text-[15px] text-gray-600">Advanced customization</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-blue-500 flex-shrink-0 mr-2 mt-0.5" />
                      <span className="text-sm sm:text-[15px] text-gray-600">Dedicated onboarding help</span>
                    </li>
                  </ul>
                </div>
              </div>
            </AnimatedSection>
          </div>

          <div className="mt-8 sm:mt-10 md:mt-12 text-center">
            <p className="text-xs sm:text-[15px] text-gray-500 max-w-lg mx-auto">All plans include a 90-day free trial. No credit card required. Cancel anytime.</p>
          </div>
        </div>
      </section>

      {/* CTA Section - Jobs-inspired simplicity */}
      <section className="py-16 sm:py-20 md:py-28 px-4 sm:px-6">
        <div className="max-w-[1120px] mx-auto">
          <motion.div 
            className="rounded-xl p-8 sm:p-10 md:p-14 text-center overflow-hidden relative bg-gradient-to-br from-blue-600 via-blue-600 to-blue-700"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: "-10%" }}
          >
            {/* Subtle light effect */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -right-32 -top-32 w-64 sm:w-96 h-64 sm:h-96 bg-white/10 rounded-full blur-3xl" />
              <div className="absolute -left-32 -bottom-32 w-64 sm:w-96 h-64 sm:h-96 bg-white/5 rounded-full blur-3xl" />
            </div>

            <div className="relative z-10 max-w-xl mx-auto">
              <motion.h2 
                className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-5 text-white tracking-tight"
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                One Click Away
              </motion.h2>
              <motion.p 
                className="text-base sm:text-[17px] text-blue-50 mb-6 sm:mb-8 max-w-md mx-auto leading-relaxed"
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                90 days from now, you'll wish you had started today. Get your nights and weekends back. Remember what it feels like to run your business, not have it run you.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="space-y-3 sm:space-y-4"
              >
                <Button 
                  size="lg"
                  onClick={handleGetStarted}
                  className="bg-white text-blue-600 hover:bg-blue-50 shadow-lg shadow-blue-800/20 w-full sm:w-auto px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-[17px]"
                >
                  90 Days Free
                </Button>
                <div className="text-xs sm:text-[15px] text-blue-100 font-light">
                  90 days free. Hear the difference yourself.
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-100 py-12 sm:py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
            <div className="sm:col-span-2 md:col-span-1">
              <div className="flex items-center mb-4 sm:mb-5">
                <span className="text-lg font-medium text-gray-900">Savant</span>
              </div>
              <p className="text-sm text-gray-600 mb-5 sm:mb-6">
                Intelligent automation for growing businesses.
              </p>
              <div className="text-xs sm:text-sm text-gray-500">
                © {new Date().getFullYear()} Savant AI. All rights reserved.
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3 sm:mb-4">Product</h3>
              <ul className="space-y-2">
                <li><a href="#features" className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors duration-300">Features</a></li>
                <li><a href="#pricing" className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors duration-300">Pricing</a></li>
                <li><a href="#" className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors duration-300">Integrations</a></li>
                <li><a href="#" className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors duration-300">Updates</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3 sm:mb-4 mt-6 sm:mt-0">Company</h3>
              <ul className="space-y-2">
                <li><a href="#" className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors duration-300">About</a></li>
                <li><a href="#" className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors duration-300">Blog</a></li>
                <li><a href="#" className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors duration-300">Careers</a></li>
                <li><a href="#" className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors duration-300">Contact</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3 sm:mb-4 mt-6 sm:mt-0">Resources</h3>
              <ul className="space-y-2">
                <li><a href="#" className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors duration-300">Documentation</a></li>
                <li><a href="#" className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors duration-300">Help Center</a></li>
                <li><a href="#" className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors duration-300">Privacy</a></li>
                <li><a href="#" className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors duration-300">Terms</a></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage; 